import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import confetti from "canvas-confetti";
import { api, formatApiError } from "../lib/api";
import { createFirestoreDoc } from "../lib/firebase";
import { sendEmailNotification, buildBookingEmailTemplate } from "../lib/emailService";
import { RippleButton } from "./RippleButton";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { MapPin, Check, X, Loader2, Calendar, Clock, Sparkles, Dog, Cat, Heart } from "lucide-react";

const SERVICE_GROUPS = [
  {
    category: "🐶 Grooming Packages",
    items: [
      { name: "Bath & Fluff Dry Spa", price: 799 },
      { name: "Puppy First Groom", price: 1199 },
      { name: "Essential Care", price: 1299 },
      { name: "Full Grooming & Styling", price: 1399 },
      { name: "Senior Pet Care", price: 1799 },
      { name: "Royal Ultra-Luxury Spa", price: 1899 },
      { name: "Premium Spa (Most Popular)", price: 1999 },
      { name: "Luxury Spa Experience", price: 2799 },
    ],
  },
  {
    category: "⭐ Care Plan Subscriptions",
    items: [
      { name: "Monthly Care Plan", price: 2999, label: "Monthly Care Plan (₹2,999/mo)" },
      { name: "Quarterly Care Plan", price: 7999, label: "Quarterly Care Plan (₹7,999/qtr)" },
      { name: "Annual VIP Care Plan", price: 14999, label: "Annual VIP Care Plan (₹14,999/yr)" },
    ],
  },
  {
    category: "👑 Membership Plans",
    items: [
      { name: "Silver Membership", price: 2999, label: "Silver Membership (₹2,999/yr)" },
      { name: "Gold Membership", price: 5999, label: "Gold Membership (₹5,999/yr)" },
      { name: "Platinum Membership", price: 9999, label: "Platinum Membership (₹9,999/yr)" },
    ],
  },
  {
    category: "✨ Add-On Spa Treatments",
    items: [
      { name: "Eye Cleaning", price: 149 },
      { name: "Deodorizing Spray", price: 149 },
      { name: "Paw Balm Treatment", price: 199 },
      { name: "Face Trim", price: 249 },
      { name: "Teeth Freshening", price: 399 },
      { name: "Coat Conditioning", price: 499 },
      { name: "Bandana", price: 499 },
      { name: "Bow Tie", price: 499 },
      { name: "Flea & Tick Treatment", price: 1199 },
    ],
  },
];

const PRICE = {
  // Grooming Packages
  "Bath & Fluff Dry Spa": 799,
  "Puppy First Groom": 1199,
  "Essential Care": 1299,
  "Full Grooming & Styling": 1399,
  "Senior Pet Care": 1799,
  "Royal Ultra-Luxury Spa": 1899,
  "Premium Spa (Most Popular)": 1999,
  "Premium Spa": 1999,
  "Luxury Spa Experience": 2799,

  // Care Plan Subscriptions
  "Monthly Care Plan": 2999,
  "Monthly Care Plan (₹2,999/mo)": 2999,
  "Quarterly Care Plan": 7999,
  "Quarterly Care Plan (₹7,999/qtr)": 7999,
  "Annual VIP Care Plan": 14999,
  "Annual VIP Care Plan (₹14,999/yr)": 14999,

  // Membership Plans
  "Silver Membership": 2999,
  "Silver Membership (₹2,999/yr)": 2999,
  "Gold Membership": 5999,
  "Gold Membership (₹5,999/yr)": 5999,
  "Platinum Membership": 9999,
  "Platinum Membership (₹9,999/yr)": 9999,

  // Add-Ons
  "Paw Balm Treatment": 199,
  "Paw Balm Treatment (₹199)": 199,
  "Eye Cleaning": 149,
  "Eye Cleaning (₹149)": 149,
  "Deodorizing Spray": 149,
  "Deodorizing Spray (₹149)": 149,
  "Face Trim": 249,
  "Face Trim (₹249)": 249,
  "Teeth Freshening": 399,
  "Teeth Freshening (₹399)": 399,
  "Coat Conditioning": 499,
  "Coat Conditioning (₹499)": 499,
  "Bandana": 499,
  "Bandana (₹499)": 499,
  "Bow Tie": 499,
  "Bow Tie (₹499)": 499,
  "Flea & Tick Treatment": 1199,
  "Flea & Tick Treatment (₹1,199)": 1199,
};

const ALL_SERVICES_LIST = SERVICE_GROUPS.flatMap((g) => g.items.map((i) => i.name));

const TIMES = [
  "09:00 AM", "09:30 AM", "10:00 AM", "10:30 AM", 
  "11:00 AM", "11:30 AM", "12:00 PM", "12:30 PM", 
  "01:00 PM", "01:30 PM", "02:00 PM", "02:30 PM", 
  "03:00 PM", "03:30 PM", "04:00 PM", "04:30 PM", 
  "05:00 PM", "05:30 PM", "06:00 PM", "06:30 PM", 
  "07:00 PM"
];

const EMPTY_FORM = {
  pet_name: "",
  owner_name: "",
  phone: "",
  email: "",
  pet_type: "Dog",
  breed: "",
  pet_weight: "",
  service: "Full Grooming & Styling",
  preferred_date: "",
  preferred_time: "09:00 AM",
  address: "",
  special_instructions: "",
  coupon_code: ""
};

export default function BookingModal({ isOpen, onClose, initialService = "", initialCoupon = "" }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [step, setStep] = useState("form"); // form | summary | success
  const [loading, setLoading] = useState(false);
  const [discount, setDiscount] = useState(0);

  useEffect(() => {
    if (isOpen) {
      const todayStr = new Date().toISOString().split("T")[0];
      setForm((prev) => {
        let matchedService = prev.service;
        const safeService = typeof initialService === "string" ? initialService : "";
        const safeCoupon = typeof initialCoupon === "string" ? initialCoupon : "";

        if (safeService) {
          const exactMatch = ALL_SERVICES_LIST.find(
            (s) => s.toLowerCase() === safeService.toLowerCase() || s.toLowerCase().includes(safeService.toLowerCase()) || safeService.toLowerCase().includes(s.toLowerCase())
          );
          if (exactMatch) matchedService = exactMatch;
          else matchedService = safeService;
        }
        return {
          ...EMPTY_FORM,
          service: matchedService,
          coupon_code: safeCoupon || prev.coupon_code || "",
          preferred_date: prev.preferred_date || todayStr,
        };
      });
      setStep("form");
      setDiscount(0);
      const safeCoupon = typeof initialCoupon === "string" ? initialCoupon : "";
      if (safeCoupon) {
        if (safeCoupon.toUpperCase() === "FIRSTGROOM") setDiscount(20);
        else if (safeCoupon.toUpperCase() === "WOOFSY200") setDiscount(15);
      }
    }
  }, [isOpen, initialService, initialCoupon]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const price = PRICE[form.service] || 1399;
  const total = Math.max(0, Math.round(price * (1 - discount / 100)));

  const applyCoupon = async () => {
    if (!form.coupon_code) return;
    const code = form.coupon_code.trim().toUpperCase();
    if (code === "FIRSTGROOM") {
      setDiscount(20);
      toast.success("Coupon FIRSTGROOM applied — 20% OFF!");
      return;
    }
    if (code === "WOOFSY200" || code === "REFER200") {
      setDiscount(15);
      toast.success("Referral discount applied — ₹200 OFF!");
      return;
    }
    try {
      const { data } = await api.post("/coupons/validate", { code });
      if (data.valid) {
        setDiscount(data.discount_percent);
        toast.success(`Coupon applied — ${data.discount_percent}% off!`);
      } else {
        setDiscount(0);
        toast.error("Invalid coupon code");
      }
    } catch {
      setDiscount(10);
      toast.success(`Coupon ${code} applied!`);
    }
  };

  const goSummary = (e) => {
    e.preventDefault();
    if (!form.pet_name || !form.owner_name || !form.phone || !form.email || !form.preferred_date || !form.address) {
      toast.error("Please fill all required fields");
      return;
    }
    setStep("summary");
  };

  const confirmBooking = async () => {
    setLoading(true);
    try {
      const payload = {
        ...form,
        total_amount: total,
        discount_amount: Math.round(price * (discount / 100)),
        status: "Pending",
        created_at: new Date().toISOString(),
      };

      // 1. Save to Firebase Firestore
      await createFirestoreDoc("bookings", payload);

      // 2. Also save customer entry to Firebase Firestore
      await createFirestoreDoc("customers", {
        name: form.owner_name,
        email: form.email,
        phone: form.phone,
        pet_name: form.pet_name,
        breed: form.breed || form.pet_type,
        address: form.address,
      }).catch(() => {});

      // 3. Send Emails to User + Admin with Logo, Website URL and WhatsApp button
      const userEmailHtml = buildBookingEmailTemplate(payload, false);
      const adminEmailHtml = buildBookingEmailTemplate(payload, true);

      sendEmailNotification({
        to: form.email,
        subject: `🐾 Woofsy Booking Confirmation - ${form.pet_name}`,
        html: userEmailHtml,
      }).catch(() => {});

      sendEmailNotification({
        to: "info@woofsy.co.in",
        subject: `🚨 New Booking Received: ${form.pet_name} (${form.owner_name})`,
        html: adminEmailHtml,
      }).catch(() => {});

      // 4. Fallback sync to backend API if running
      await api.post("/bookings", form).catch(() => {});

      setStep("success");
      confetti({ particleCount: 160, spread: 90, origin: { y: 0.5 }, colors: ["#FF8A3D", "#9EDCFF", "#9FE870"] });
      setTimeout(() => confetti({ particleCount: 80, angle: 60, spread: 70, origin: { x: 0.2 } }), 250);
      setTimeout(() => confetti({ particleCount: 80, angle: 120, spread: 70, origin: { x: 0.8 } }), 400);

      const text = `*New Booking Request!* 🐾
*Owner:* ${form.owner_name}
*Phone:* ${form.phone}
*Email:* ${form.email}
*Pet:* ${form.pet_name} (${form.pet_type}${form.pet_type !== "Cat" && form.breed ? `, ${form.breed}` : ""})
${form.pet_type !== "Cat" ? `*Service:* ${form.service}\n` : ""}*Date:* ${form.preferred_date}
*Time:* ${form.preferred_time}
*Address:* ${form.address}
*Instructions:* ${form.special_instructions || "None"}`;

      const waUrl = `https://api.whatsapp.com/send/?phone=918421114134&text=${encodeURIComponent(text)}&type=phone_number&app_absent=0`;
      window.open(waUrl, "_blank");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to confirm booking");
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    setForm(EMPTY_FORM);
    setDiscount(0);
    setStep("form");
    onClose();
  };

  const todayStr = new Date().toISOString().split("T")[0];

  return (
    <AnimatePresence>
      <div
        data-lenis-prevent="true"
        data-lenis-prevent-wheel="true"
        className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-6 overflow-y-auto overscroll-contain bg-slate-950/60 backdrop-blur-md"
      >
        <motion.div
          data-lenis-prevent="true"
          data-lenis-prevent-wheel="true"
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ duration: 0.25 }}
          className="relative w-full max-w-xl bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden my-auto max-h-[95dvh] sm:max-h-[88vh] flex flex-col"
        >
          {/* Header Bar */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-orange-50/60 via-white to-amber-50/60 shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-woofsy-primary/15 text-woofsy-primary flex items-center justify-center font-bold">
                <Sparkles className="w-5 h-5 text-woofsy-primary" />
              </div>
              <div>
                <h3 className="font-heading font-extrabold text-lg text-slate-900 leading-tight">Book Dog Grooming</h3>
                <p className="text-xs text-slate-500 font-medium">Doorstep mobile spa service in Pune in 30 mins</p>
              </div>
            </div>
            <button
              onClick={onClose}
              data-testid="close-booking-modal"
              className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-600 flex items-center justify-center transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Modal Body */}
          <div
            data-lenis-prevent="true"
            data-lenis-prevent-wheel="true"
            className="p-4 sm:p-5 lg:p-6 overflow-y-auto flex-1 touch-pan-y overscroll-contain"
          >
            <AnimatePresence mode="wait">
              {step === "form" && (
                <motion.form
                  key="modal-form"
                  onSubmit={goSummary}
                  data-testid="modal-booking-form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="space-y-4"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="Pet Name *">
                      <Input
                        data-testid="modal-input-pet-name"
                        value={form.pet_name}
                        onChange={(e) => set("pet_name", e.target.value)}
                        placeholder="e.g. Coco"
                        required
                      />
                    </Field>

                    <Field label="Owner Name *">
                      <Input
                        data-testid="modal-input-owner-name"
                        value={form.owner_name}
                        onChange={(e) => set("owner_name", e.target.value)}
                        placeholder="Your name"
                        required
                      />
                    </Field>

                    <Field label="Phone Number *">
                      <Input
                        data-testid="modal-input-phone"
                        value={form.phone}
                        onChange={(e) => set("phone", e.target.value)}
                        placeholder="+91 98765 43210"
                        required
                      />
                    </Field>

                    <Field label="Email Address *">
                      <Input
                        data-testid="modal-input-email"
                        type="email"
                        value={form.email}
                        onChange={(e) => set("email", e.target.value)}
                        placeholder="you@email.com"
                        required
                      />
                    </Field>

                    <Field label="Pet Category">
                      <div className="flex gap-2">
                        {[
                          { type: "Dog", icon: "🐶" },
                          { type: "Puppy", icon: "🐾" },
                          { type: "Cat", icon: "🐱" },
                        ].map((t) => (
                          <button
                            type="button"
                            key={t.type}
                            data-testid={`modal-pet-type-${t.type.toLowerCase()}`}
                            onClick={() => set("pet_type", t.type)}
                            className={`flex-1 rounded-xl py-2 px-3 text-xs font-bold border transition-all flex items-center justify-center gap-1.5 ${
                              form.pet_type === t.type
                                ? "bg-woofsy-primary text-white border-woofsy-primary shadow-sm"
                                : "bg-slate-50 border-slate-200 text-slate-700 hover:bg-slate-100"
                            }`}
                          >
                            <span>{t.icon}</span>
                            <span>{t.type}</span>
                          </button>
                        ))}
                      </div>
                    </Field>

                    {form.pet_type !== "Cat" && (
                      <>
                        <Field label="Breed">
                          <Input
                            data-testid="modal-input-breed"
                            value={form.breed}
                            onChange={(e) => set("breed", e.target.value)}
                            placeholder="e.g. Shih Tzu, Golden"
                          />
                        </Field>

                        <Field label="Pet Weight">
                          <Input
                            data-testid="modal-input-weight"
                            value={form.pet_weight}
                            onChange={(e) => set("pet_weight", e.target.value)}
                            placeholder="e.g. 7 kg"
                          />
                        </Field>

                        <Field label="Service / Subscription Required *">
                          <select
                            data-testid="modal-select-service"
                            value={form.service}
                            onChange={(e) => set("service", e.target.value)}
                            className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-woofsy-primary shadow-sm"
                          >
                            {SERVICE_GROUPS.map((group) => (
                              <optgroup key={group.category} label={group.category}>
                                {group.items.map((item) => (
                                  <option key={item.name} value={item.name}>
                                    {item.label || `${item.name} — ₹${item.price.toLocaleString("en-IN")}`}
                                  </option>
                                ))}
                              </optgroup>
                            ))}
                          </select>
                        </Field>
                      </>
                    )}

                    <Field label="Preferred Date *">
                      <Input
                        data-testid="modal-input-date"
                        type="date"
                        min={todayStr}
                        value={form.preferred_date}
                        onChange={(e) => set("preferred_date", e.target.value)}
                        required
                      />
                    </Field>

                    <Field label="Preferred Time Slot">
                      <select
                        data-testid="modal-select-time"
                        value={form.preferred_time}
                        onChange={(e) => set("preferred_time", e.target.value)}
                        className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm font-medium text-slate-800 focus:outline-none focus:ring-2 focus:ring-woofsy-primary"
                      >
                        {TIMES.map((t) => (
                          <option key={t} value={t}>
                            {t}
                          </option>
                        ))}
                      </select>
                    </Field>
                  </div>

                  {/* Address */}
                  <Field label="Doorstep Address in Pune *">
                    <Input
                      data-testid="modal-input-address"
                      value={form.address}
                      onChange={(e) => set("address", e.target.value)}
                      placeholder="Flat/House No, Building, Street, Society, Pincode"
                      required
                    />
                  </Field>

                  {/* Special Instructions */}
                  <Field label="Special Instructions (Optional)">
                    <Textarea
                      data-testid="modal-input-instructions"
                      value={form.special_instructions}
                      onChange={(e) => set("special_instructions", e.target.value)}
                      placeholder="Any skin allergies, nervous pet behavior, or parking instructions?"
                      rows={2}
                    />
                  </Field>

                  {/* Coupon Code */}
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Field label="Promo / Coupon Code">
                        <Input
                          data-testid="modal-input-coupon"
                          value={form.coupon_code}
                          onChange={(e) => set("coupon_code", e.target.value)}
                          placeholder="e.g. FIRSTGROOM, WOOFSY200"
                        />
                      </Field>
                    </div>
                    <RippleButton
                      type="button"
                      data-testid="modal-apply-coupon-btn"
                      variant="primary"
                      className="px-5 py-2.5 text-xs uppercase tracking-wider shrink-0"
                      onClick={applyCoupon}
                    >
                      Apply
                    </RippleButton>
                  </div>

                  {/* Footer Action Row */}
                  <div className="pt-4 border-t border-slate-100 flex items-center justify-between gap-4">
                    <div>
                      <span className="text-xs text-slate-500 font-medium block">Total Payable</span>
                      <p className="font-heading font-extrabold text-2xl text-slate-900 flex items-baseline gap-2">
                        <span>₹{total.toLocaleString("en-IN")}</span>
                        {discount > 0 && (
                          <span className="text-xs text-slate-400 line-through">₹{price.toLocaleString("en-IN")}</span>
                        )}
                        {discount > 0 && (
                          <span className="text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">{discount}% OFF</span>
                        )}
                      </p>
                    </div>

                    <RippleButton
                      type="submit"
                      data-testid="modal-review-booking-btn"
                      variant="primary"
                      className="px-8 py-3.5 text-sm"
                    >
                      Review Booking &gt;
                    </RippleButton>
                  </div>
                </motion.form>
              )}

              {step === "summary" && (
                <motion.div
                  key="modal-summary"
                  data-testid="modal-booking-summary"
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0 }}
                  className="space-y-5"
                >
                  <div>
                    <h3 className="font-heading font-bold text-xl text-slate-900 mb-1">Confirm Booking Summary</h3>
                    <p className="text-xs text-slate-500">Please verify your details before confirming doorstep booking.</p>
                  </div>

                  <div className="space-y-3 rounded-2xl bg-slate-50 border border-slate-200/80 p-5 text-sm">
                    {[
                      ["Pet Details", `${form.pet_name} (${form.pet_type}${form.breed ? `, ${form.breed}` : ""})`],
                      ["Owner Name", form.owner_name],
                      ["Phone Number", form.phone],
                      ["Email Address", form.email],
                      ["Selected Service", form.service],
                      ["Scheduled Date", form.preferred_date],
                      ["Time Slot", form.preferred_time],
                      ["Doorstep Address", form.address],
                    ].map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-4">
                        <span className="text-slate-500 shrink-0 font-medium">{k}</span>
                        <span className="font-bold text-slate-900 text-right">{v || "-"}</span>
                      </div>
                    ))}
                    <div className="pt-3 border-t border-slate-200 flex justify-between items-center">
                      <span className="font-heading font-extrabold text-slate-900">Total Payable</span>
                      <span className="font-heading font-extrabold text-xl text-woofsy-primary">₹{total}</span>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <RippleButton
                      variant="ghost"
                      data-testid="modal-edit-booking-btn"
                      className="flex-1 py-3 text-sm"
                      onClick={() => setStep("form")}
                    >
                      Edit Details
                    </RippleButton>
                    <RippleButton
                      variant="primary"
                      data-testid="modal-confirm-booking-btn"
                      className="flex-1 py-3 text-sm"
                      onClick={confirmBooking}
                      disabled={loading}
                    >
                      {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Confirm Booking"}
                    </RippleButton>
                  </div>
                </motion.div>
              )}

              {step === "success" && (
                <motion.div
                  key="modal-success"
                  data-testid="modal-booking-success"
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-6 space-y-4"
                >
                  <div className="w-20 h-20 rounded-full bg-orange-100 text-woofsy-primary flex items-center justify-center mx-auto text-4xl shadow-inner">
                    🐶
                  </div>

                  <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 px-4 py-1.5 text-xs font-extrabold border border-emerald-200">
                    <Check className="w-4 h-4 stroke-[3]" />
                    <span>BOOKING CONFIRMED</span>
                  </div>

                  <h3 className="font-heading font-extrabold text-2xl text-slate-900">
                    Your Woofsy Groomer is Booked! 🎉
                  </h3>
                  <p className="text-xs text-slate-600 max-w-md mx-auto leading-relaxed">
                    We have scheduled your doorstep grooming session for <b>{form.pet_name}</b> on <b>{form.preferred_date}</b> at <b>{form.preferred_time}</b>. A confirmation email has been sent to <b>{form.email}</b>.
                  </p>

                  <div className="pt-4 flex justify-center">
                    <RippleButton
                      variant="primary"
                      data-testid="modal-done-btn"
                      className="px-8 py-3 text-sm font-bold"
                      onClick={resetAndClose}
                    >
                      Done / Close
                    </RippleButton>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs font-bold text-slate-700 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
